import { store } from '@redux/store'
import { setOrder, setPaymentInvalidFields } from '@redux/modules/checkout'
import { abbreviateState } from '@helpers/geo-location'
import { titleCase, validateZip, validateEmail } from '@helpers/string-helper'
import { getOrder } from '../global'
import { parseJwt } from './credit-card'

export const getActualPaymentType = ({ paymentType: type, paymentProperties = {} }) => {
  const actualPaymentTypes = {
    ACIMA: 'Acima',
    AFF: 'Affirm',
    APP: 'Apple Pay',
    bitpay: 'bitpay',
    BIT: 'bitpay',
    DBUY: 'Digital Buy',
    FIN: 'Rooms To Go Finance',
    GEN: 'Genesis',
    GIFT: 'Gift Card',
    KLN: 'Klarna',
    PALV2: 'PayPal',
    VISA: 'Visa Checkout',
    WFG: 'Wells Fargo',
  }

  const paymentTypes = Object.keys(actualPaymentTypes)
  return paymentTypes.includes(type) || (['CYBERV3', 'CYBERV4'].includes(type) && paymentProperties?.token) ? type : ''
}

export const getAppliedPayments = payments => {
  const appliedPayments = []
  payments.forEach(p => {
    if (p.paymentType !== 'CYBERV4') {
      appliedPayments.push(p)
    } else {
      p.paymentProperties.forEach(({ authAmount, billingInfo, card, token }) => {
        appliedPayments.push({
          authorized: false,
          authorizedAmount: authAmount,
          paymentProperties: { billingInfo, card, token },
          paymentType: 'CYBERV4',
        })
      })
    }
  })

  return appliedPayments
}

export const getPaymentCondensedAddressInfo = (
  { billingAddress = {}, contact, payer = {}, shippingAddress },
  type,
  payment,
) => {
  let addrInfo

  if (type === 'PALV2' && payment?.paymentProperties?.billingAddress) {
    const { billingAddress: billingInfo } = payment.paymentProperties
    const { address1, address2, city, state, zip } = billingInfo
    const { firstName, lastName } = payment?.paymentProperties?.payerName ?? payer
    addrInfo = { firstName, lastName, address1, address2, city, state, zip }
  } else if (type === 'CYBERV4') {
    const { billingInfo } = payment.paymentProperties
    const { firstName, lastName, street1: address1, street2: address2, city, state, postalCode: zip } = billingInfo
    addrInfo = { firstName, lastName, address1, address2, city, state, zip }
  } else if (type === 'DBUY' && payment?.paymentProperties?.billingAddress) {
    const { billingAddress: billingInfo } = payment.paymentProperties
    const { address1, address2, city, state, zip } = billingInfo
    const { firstName, lastName } = payer
    addrInfo = { firstName, lastName, address1, address2, city, state, zip }
  } else {
    const hasBillingAddress =
      ((payer.billingDifferent && payer.billingSubmitted) || ['APP', 'DBUY', 'PALV2'].includes(type)) &&
      payer?.firstName &&
      payer?.lastName &&
      billingAddress?.address1

    addrInfo = hasBillingAddress
      ? {
          firstName: payer.firstName,
          lastName: payer.lastName,
          address1: billingAddress.address1,
          address2: billingAddress.address2,
          city: billingAddress.city,
          state: billingAddress.state,
          zip: billingAddress.zip,
        }
      : {
          firstName: contact.firstName,
          lastName: contact.lastName,
          address1: shippingAddress.address1,
          address2: shippingAddress.address2,
          city: shippingAddress.city,
          state: shippingAddress.state,
          zip: shippingAddress.zip,
        }
  }

  return {
    displayName: `${addrInfo.firstName} ${addrInfo.lastName}`,
    displayAddr1: titleCase(`${addrInfo.address1} ${addrInfo.address2 ?? ''}`),
    displayAddr2: `${titleCase(addrInfo.city)} ${abbreviateState(addrInfo.state)} ${addrInfo.zip}`,
    zip: addrInfo.zip,
  }
}

export const getRemainingTotal = (
  deliveryComplete = true,
  prevTotal = null,
  isCheckout = false,
  applyFinance = true,
  shippingComplete = true,
) => {
  // TODO - compare to duplicate function in @helpers/checkout/global'
  const order = getOrder()
  let total = 0
  let appliedGiftCards = 0
  let appliedFinance = 0
  if (prevTotal) {
    total = prevTotal
  }
  if (order && order.total && shippingComplete && isCheckout) {
    total = order.total
    if (!deliveryComplete && order.tax) {
      total -= order.tax
    }
    if (deliveryComplete) {
      if (order.paymentInfo && order.paymentInfo.length > 0) {
        appliedGiftCards = order.paymentInfo
          .filter(payment => payment.paymentType === 'GIFT')
          .reduce((sum, gc) => sum + gc.authorizedAmount, 0)

        if (appliedGiftCards > 0) {
          total -= appliedGiftCards
        }

        const rtgFin = order.paymentInfo.filter(p => ['DBUY', 'FIN', 'GEN'].includes(p.paymentType))
        if (rtgFin.length > 0 && applyFinance) {
          appliedFinance = rtgFin[0].authorizedAmount
          total -= appliedFinance
        }
      }
    }
  }
  return {
    total,
    gift: appliedGiftCards,
    fin: appliedFinance,
    tax: order && order.tax,
    deliveryCost: order && order.totalDeliveryCharge,
    totalSavings: order && order.promotions && order.promotions.totalSavings,
  }
}

export const getInfoWithGiftAndFinance = () => {
  const { paymentInfo = [] } = getOrder()
  const giftFin = paymentInfo?.filter(
    payment =>
      ['DBUY', 'FIN', 'GEN', 'GIFT', 'CYBERV4', 'WFG'].includes(payment.paymentType) && payment.paymentProperties,
  )
  return giftFin
}

export const setPayerInfo = (info, field) => {
  let order = getOrder()
  if (field) {
    order = {
      ...order,
      payer: {
        ...order.payer,
        [field]: info,
      },
    }
  } else {
    order = {
      ...order,
      payer: {
        ...order.payer,
        ...info,
      },
    }
  }
  store.dispatch(setOrder(order))
}

// eslint-disable-next-line
export let validatePaymentStep = nextStep => {
  const order = getOrder()
  const invalidFields = []
  if (order && order.payer && order.payer.billingDiffferent && nextStep === 'review') {
    const entries = Object.entries({
      ...order.billingAddress,
      ...order.payer,
    })
    for (let i = 0; i < entries.length; i++) {
      const entryKey = entries[i][0]
      const entryData = entries[i][1]
      if (
        (entryData === '' || entryData === null) &&
        entryKey !== 'address2' &&
        entryKey !== 'phone' &&
        entryKey !== 'email'
      ) {
        invalidFields.push(entryKey)
      } else {
        if (entryKey === 'email' && !validateEmail(entryData)) {
          invalidFields.push('email')
        }
        if (entryKey === 'zip' && !validateZip(entryData)) {
          invalidFields.push('zip')
        }
      }
    }
  }
  let validated = true
  if ((order && order.amountDue > 0 && nextStep === 'review') || invalidFields.length > 0) {
    validated = false
  }
  if (!invalidFields.length && !validated) {
    invalidFields.push('A payment has not been submitted.')
  }
  store.dispatch(setPaymentInvalidFields(invalidFields))
  return invalidFields
}

/**
 * Removes the 4 'klarna_' prefixed sessionStorage variables assigned when a Klarna payment is created
 */
export const removeKlarnaSession = () => {
  sessionStorage.removeItem('klarna_auth_total')
  sessionStorage.removeItem('klarna_session_id')
  sessionStorage.removeItem('klarna_session_order')
  sessionStorage.removeItem('klarna_session_token')
}

export const getPaymentDisplayInfo = (type, payment) => {
  switch (type) {
    case 'ACIMA':
      return {
        paymentName: 'Acima Leasing',
        subTitle: '',
      }
    case 'AFF':
      return {
        paymentName: 'Affirm',
        subTitle: '',
      }
    case 'APP':
      return {
        paymentName: 'ApplePay',
        subTitle: 'Paid with card',
      }
    case 'bitpay':
    case 'BIT':
      return {
        paymentName: 'Bitpay',
        subTitle: '',
      }
    case 'DBUY': {
      const financePlan = payment.paymentProperties?.financePlan
      const financePlanFullTitle = financePlan.includes('GENESIS')
        ? 'Genesis'
        : `${financePlan.substring(0, 2)} Months Financing`
      return {
        paymentName: `RTG Card x${payment.paymentProperties?.accountNumber?.replaceAll('X', '')}`,
        subTitle: financePlanFullTitle,
      }
    }
    case 'FIN':
      return {
        paymentName: 'RTG Financing',
        subTitle: '',
      }
    case 'GEN':
      return {
        paymentName: 'Concora Credit',
        subTitle: '',
      }
    case 'GIFT': {
      const cardNumber = payment.paymentProperties?.cardNumber
      const credit = payment.authorizedAmount
      const balance = payment.paymentProperties?.balance
      const remainingGiftBalance = (balance - credit).toFixed(2).replace('.00', '')
      const newLine = remainingGiftBalance.length >= 3
      return {
        paymentName: `Gift Card x${cardNumber.substring(cardNumber.length - 4, cardNumber.length)}`,
        subTitle: `Credit: $${credit},${newLine ? '\n' : ' '}Balance: $${remainingGiftBalance}`,
      }
    }
    case 'KLN':
      return { paymentName: 'Klarna', subTitle: '' }
    case 'PALV2':
      return { paymentName: `PayPal`, subTitle: payment.paymentProperties?.payerEmail }
    case 'CYBERV3':
    case 'CYBERV4': {
      const card = payment.paymentProperties?.card
      const cardName = card?.cardName
      const cardExpirationDate = `Exp. ${card?.expirationMonth}/${card?.expirationYear}`
      const { maskedCardNum } = payment?.paymentProperties?.token ? parseJwt(payment.paymentProperties.token) : {}

      return {
        paymentName: `${cardName[0].toUpperCase()}${cardName.slice(1)} ${maskedCardNum || ''}`,
        subTitle: cardExpirationDate,
      }
    }
    case 'WFG':
      return { paymentName: 'Rooms To Go Credit Card By Wells Fargo', subTitle: '' }
    default:
      return { paymentName: 'Payment', subTitle: '' }
  }
}
